import { DiContainer, Injectable, InjectByToken } from '@jkhy/frontend-utils/di';
import { exists, isNonEmptyString, SessionStorageService, WindowService } from '@treasury/utils';
import { LEGACY_BO_API_ROOT, LEGACY_CHANNEL_API_ROOT } from './api-paths';
import {
    AppType,
    AppTypeToken,
    BuildType,
    BuildTypeToken,
    Environment,
    InjectedWindow,
    INSTITUTION_ID,
    INSTITUTION_UNIVERSAL_ID,
} from './configuration.types';

// this code exists because Vite does not support CommonJS imports in build mode
// even with the viteCommonjs plugin that supports it in dev server;
// for now, this is a stopgap that simply appears to have no vanity URLs
// TODO: add plugin that supports CommonJS to Vite config
let vanityUrlMap = new Map<string, string>();
try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { vanityUrlToIdMap } = require('./vanity-url-map');
    vanityUrlMap = vanityUrlToIdMap;
} catch {
    // eslint-disable-next-line no-console
    console.warn(
        'Could not load vanity URL map because require was not defined. Defaulting to empty map.'
    );
}

/**
 * Service exposing information used for bootstrapping or
 * otherwise configuring channel and BO applications.
 */
@Injectable()
export class ConfigurationService {
    constructor(
        @InjectByToken(AppTypeToken) public readonly app: AppType,
        @InjectByToken(BuildTypeToken) public readonly buildType: BuildType,
        private readonly window: WindowService,
        private readonly session: SessionStorageService
    ) {
        if (app === AppType.Unknown) {
            throw new Error('Could not determine application type.');
        }
    }

    /**
     * Convenience method for locations where DI isn't available.
     */
    public static async getInstance() {
        return (await DiContainer.getInstance()).get(ConfigurationService);
    }

    public readonly uisRedirectUrl = this.generateSsoCallbackUrl;

    public institutionApiRoot?: string;

    /**
     * The API path used when the client is deployed together with the API on the same domain.
     */
    private readonly apiPath =
        this.app === AppType.Channel ? LEGACY_CHANNEL_API_ROOT : LEGACY_BO_API_ROOT;

    public get institutionId() {
        const path = this.window.location.pathname.split('/').filter(isNonEmptyString);
        if (!path) {
            throw new Error('Incomplete path. Unable to determine institutionId.');
        }
        if (this.app === AppType.Channel) {
            return path[0];
        }
        if (this.app === AppType.Pwa) {
            return path[1];
        }
        if (this.app === AppType.BackOffice) {
            return this.session.get(INSTITUTION_ID) ?? '';
        }
        return '';
    }

    public set institutionId(value: string) {
        if (exists(value)) {
            this.session.set(INSTITUTION_ID, value);
        }
    }

    public get institutionUniversalId() {
        return this.session.get(INSTITUTION_UNIVERSAL_ID) ?? '';
    }

    public set institutionUniversalId(value: string) {
        if (exists(value)) {
            this.session.set(INSTITUTION_UNIVERSAL_ID, value);
        }
    }

    /**
     * The root URL prepended to all calls made to API endpoints.
     */
    public get apiRoot() {
        const { apiRoot } = this.injectedConfig;

        const trueApiRoot = this.institutionApiRoot ?? apiRoot;

        return isNonEmptyString(trueApiRoot) ? trueApiRoot : `${this.baseUrl}/${this.apiPath}`;
    }

    public get analyticsToken() {
        return this.injectedConfig.analyticsToken;
    }

    /**
     * The base path of the application.
     */
    public get basePath() {
        return this.injectedConfig.basePath ?? '/';
    }

    /**
     * Whether the application is authenticated using banno authentication.
     */
    public get bannoAuthEnabled() {
        const value = this.injectedConfig.bannoAuthEnabled;
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const stringValue = value as string;
            if (stringValue.trim().toLowerCase() === 'true') {
                return true;
            }
            if (stringValue.trim().toLowerCase() === 'false') {
                return false;
            }
        }
        return this.injectedConfig.bannoAuthEnabled ?? false;
    }

    /**
     * Whether the application is running inside a native mobile app wrapper (e.g. iOS WKWebView).
     */
    public get isNativeApp() {
        return this.injectedConfig.isNativeApp ?? false;
    }

    public get environment() {
        if (this.injectedMetadata.isLocal) {
            return Environment.Local;
        }

        const { hostname } = this.window.location;
        if (hostname) {
            const vanityUrls = Array.from(vanityUrlMap.keys());
            if (vanityUrls.some(url => hostname === url)) {
                return Environment.Production;
            }

            switch (hostname) {
                case '127.0.0.1':
                case 'localhost':
                    return Environment.Local;
                case 'treasury.dev.jackhenry.com':
                case 'treasury-channel.banno-development.com':
                case 'treasury-channel.dev.jackhenry.com':
                case 'treasury-backoffice.dev.jackhenry.com':
                case 'treasurybackoffice.dev.jackhenry.com':
                case 'platform.banno-development.com':
                    return Environment.Dev;
                case 'treasury.stg.jackhenry.com':
                case 'treasury-channel.banno-staging.com':
                case 'treasury-channel.stg.jackhenry.com':
                case 'treasury-backoffice.stg.jackhenry.com':
                case 'treasurybackoffice.stg.jackhenry.com':
                case 'platform.banno-staging.com':
                    return Environment.Staging;
                case 'treasury.jackhenry.com':
                case 'treasury-channel.jackhenry.com':
                case 'treasury-backoffice.jackhenry.com':
                case 'treasurybackoffice.jackhenry.com':
                case 'banno.com':
                    return Environment.Production;
                default:
                    return Environment.Unknown;
            }
        }

        return Environment.Unknown;
    }

    /**
     * The client-side ID token used by the feature flag SDK.
     */
    public get ffToken() {
        // maps an `Environment` to a LD Client-side ID
        // https://docs.launchdarkly.com/sdk/concepts/client-side-server-side#client-side-id
        switch (this.environment) {
            case Environment.Production:
                return '6633bf61801694108e04e3c2';
            case Environment.Dev:
                return '6633c04c1a652f0fc78187eb';
            case Environment.Local:
                return '665e22a09e41bb108d27a538';
            case Environment.Staging:
                return '66858f281005aa0f93eacc22';
            default:
                throw new Error('Could not retrieve LD SDK token.');
        }
    }

    public get loggingToken() {
        return this.injectedConfig.loggingToken;
    }

    public get remoteDepositToken() {
        // maps an `Environment` to a Remote Deposit SDK token
        switch (this.environment) {
            case Environment.Production:
            case Environment.Dev:
            case Environment.Staging:
                return 'eyJzaWduYXR1cmUiOiJXUkhCN0RwVVNkenMxNklTaVlpVWRnbXQ3UktXMmk2V1dRRXhDdWYvekpJKzNSa3dteHd0dnZwNTFrZGlCbUN4RzUrNStqaGNISWlkZnlzMnZwaFArMHc3VWtTSTFxQWsyc2kzUnR6S3lORkJVN1BkYXBRNWxsNFRIVEh3UzZQT0hKOURnQkpsalNOUUtrdDRxTlk0Z2ZMcEpad1pnOFdNWEJqdkFueG5NcFU2K3J1V3kwblhjZFhsbG9nTDc1R1hmdlgyVmcrU2Q4Snh6VnRjMkcrRkZiWndVa3N5QUVKUVZSbFRNNHJFYklJREsyKzdlZWgvSm1IRk9qTUM5UmVUV2h2Q3ZNMVUwbXFtV2tseWFXWnBzTGlIT0ZoTXhXSktVMDhFSWY5cmRRV1NpK2w3d2dYcGx2MDVtUG9OUTYyaEp0eUo4bGx0Ulgyam1VVVhrY1lUMEE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI4LTA0LTMwIiwiZmVhdHVyZXMiOlsiZGVwb3NpdCIsImVtIl0sImFwcGxpY2F0aW9uSWRlbnRpZmllciI6eyJ3ZWIiOlsidHJlYXN1cnkuamFja2hlbnJ5LmNvbSIsInRyZWFzdXJ5LmRldi5qYWNraGVucnkuY29tIiwidHJlYXN1cnkuc3RnLmphY2toZW5yeS5jb20iLCJ0cmVhc3VyeXZhbml0eS5kZXYuamFja2hlbnJ5LmNvbSIsInRyZWFzdXJ5dmFuaXR5LnN0Zy5qYWNraGVucnkuY29tIiwidHJlYXN1cnl2YW5pdHkuamFja2hlbnJ5LmNvbSIsImJ1c2luZXNzLmFiYmFuay5jb20iLCJidXNpbmVzc2Jhbmtpbmcub2Frc3RhcmJhbmsuY29tIiwiY2FzaG1hbmFnZW1lbnQubXlicmIuYmFuayIsImNtLnJlbGlhbmNlYmFuay5iYW5rIiwiY20ucmVsaWFuY2ViYW5rLmNvbSIsImNvbW1lcmNpYWwuYWxwaW5lYmFuay5jb20iLCJjb21tZXJjaWFsLmZpZGVsaXR5YmFua29ubGluZS5jb20iLCJjb21tZXJjaWFsLnN1bW1pdGNyZWRpdHVuaW9uLmNvbSIsImNvbm5lY3QuaW5zaWdodHRyZWFzdXJ5LmNvbSIsImNvcnBvcmF0ZW8yLm9sZHNlY29uZC5jb20iLCJlbGl0ZS5pY29uYnVzaW5lc3NiYW5rLmNvbSIsImVtZXJhbGQuc3RvbmViYW5rLmNvbSIsIm15dHJlYXN1cnkuYmFua3BsdXMubmV0IiwibXl0cmVhc3VyeS5jaXRpemVuc2ViYW5rLmNvbSIsInNhZnJhb25lY29ycG9yYXRlLnNhZnJhLmNvbSIsInNlY3VyZTEuY2FwZWNvZGZpdmUuY29tIiwic3luY2J1c2luZXNzLmJpYmFuay5jb20iLCJ0bS5hbWJhbmtpbmcuY29tIiwidG0uYmFua29ubWFpbnN0cmVldC5jb20iLCJ0bS5idXNleS5jb20iLCJ0bS5jaGVsc2Vhc3RhdGUuYmFuayIsInRtLmZpcnN0b2tsYWhvbWFiYW5rLmNvbSIsInRtLmdyb3ZlYmFua2FuZHRydXN0LmNvbSIsInRtLmxpbmtiYW5rLmNvbSIsInRtLmx1bWluYXRlLmJhbmsiLCJ0bS5ub3J0aHN0YXJhdGhvbWUuY29tIiwidG0ucGVhcGFja3ByaXZhdGUuY29tIiwidG0uc25idC5jb20iLCJ0bS53bWNiLmNvbSIsInRtYnMuZmFidC5iYW5rIiwidG1vbmxpbmUudW5pdmVzdC5uZXQiLCJ0cHMuYWx0YWJhbmsuY29tIiwidHJlYXMud2ViNS5jb20iLCJ0cmVhc3VyeS5hbHBpbmViYW5rLmNvbSIsInRyZWFzdXJ5LmFsdG9zYmFuay5jb20iLCJ0cmVhc3VyeS5hbWVyaWNhbnByaWRlLmJhbmsiLCJ0cmVhc3VyeS5hcm1zdHJvbmcuYmFuayIsInRyZWFzdXJ5LmJhbmdvci5jb20iLCJ0cmVhc3VyeS5iYW5rYnNmLmNvbSIsInRyZWFzdXJ5LmJhbmtlYXN5LmNvbSIsInRyZWFzdXJ5LmJhbmtmaXJzdGZzLmNvbSIsInRyZWFzdXJ5LmJhbmtvZm1pc3NvdXJpLmNvbSIsInRyZWFzdXJ5LmJhbmtvZm9sZG1vbnJvZS5jb20iLCJ0cmVhc3VyeS5iYW5rb2Zwb250aWFjLmNvbSIsInRyZWFzdXJ5LmJheWZpcnN0ZmluYW5jaWFsLmNvbSIsInRyZWFzdXJ5LmJvcmRlci5iYW5rIiwidHJlYXN1cnkuYnRjYmFuay5iYW5rIiwidHJlYXN1cnkuYmx1ZXNreS5iYW5rIiwidHJlYXN1cnkuY2hhaW5icmlkZ2ViYW5rLmNvbSIsInRyZWFzdXJ5LmNob2ljZW9uZS5iYW5rIiwidHJlYXN1cnkuY2hvaWNlb25lLmNvbSIsInRyZWFzdXJ5LmNsaW1hdGVmaXJzdGJhbmsuY29tIiwidHJlYXN1cnkuY29tbWVyY2VvbmViYW5rLmNvbSIsInRyZWFzdXJ5LmNyYWZ0LmJhbmsiLCJ0cmVhc3VyeS5jd2JrLmNvbSIsInRyZWFzdXJ5LmZhcm1lcnNiYW5rZ3JvdXAuY29tIiwidHJlYXN1cnkuZmNiYW5raW5nLmNvbSIsInRyZWFzdXJ5LmZmYmtjLmNvbSIsInRyZWFzdXJ5LmZpZWxkYW5kbWFpbi5jb20iLCJ0cmVhc3VyeS5maXJzdHdlc3Rlcm4uYmFuayIsInRyZWFzdXJ5LmZuYmFuay5uZXQiLCJ0cmVhc3VyeS5ncmVhdHNvdXRoZXJuYmFuay5jb20iLCJ0cmVhc3VyeS5oYXd0aG9ybmJhbmsuY29tIiwidHJlYXN1cnkuaWRhaG9maXJzdGJhbmsuY29tIiwidHJlYXN1cnkuaW50cnVzdGJhbmsuY29tIiwidHJlYXN1cnkuamJ0LmJhbmsiLCJ0cmVhc3VyeS5sYnR4LmNvbSIsInRyZWFzdXJ5Lm0xLmJhbmsiLCJ0cmVhc3VyeS5tYWJhbmsuY29tIiwidHJlYXN1cnkubWFicmV5YmFuay5jb20iLCJ0cmVhc3VyeS5tYnRnYS5jb20iLCJ0cmVhc3VyeS5tY2IuY29tIiwidHJlYXN1cnkubWlkd2VzdGJhbmsuYmFuayIsInRyZWFzdXJ5Lm15Y2VudHVyeWJhbmsuY29tIiwidHJlYXN1cnkubmFibmEuY29tIiwidHJlYXN1cnkubmJvZmkuY29tIiwidHJlYXN1cnkub3Jyc3Rvd24uY29tIiwidHJlYXN1cnkub3V0ZG9vcmJhbmsuY29tIiwidHJlYXN1cnkuc2VjdXJpdHliYW5ra2MuY29tIiwidHJlYXN1cnkuc2VjdXJpdHlmZWRlcmFsYmFuay5jb20iLCJ0cmVhc3VyeS5zbWItdC5jb20iLCJ0cmVhc3VyeS5zb25hdGFiYW5rLmNvbSIsInRyZWFzdXJ5LnNvdXRod2VzdC5iYW5rIiwidHJlYXN1cnkuc3RhdGViYW5rMTkxMC5iYW5rIiwidHJlYXN1cnkudGFuZGVtLmJhbmsiLCJ0cmVhc3VyeS50ZXhhc2JhbmsuY29tIiwidHJlYXN1cnkudHJ1c3RhcmJhbmsuY29tIiwidHJlYXN1cnkudWJtaWNoLmNvbSIsInRyZWFzdXJ5LndpbHNvbmJhbmsuY29tIiwidHJlYXN1cnltYW5hZ2VtZW50LmJhbmtvZmNhc3RpbGUuY29tIiwidHJlYXN1cnltYW5hZ2VtZW50LmV2ZXJncmVlbmJhbmtncm91cC5jb20iLCJ0cmVhc3VyeW1hbmFnZW1lbnQuZmllbGRwb2ludHByaXZhdGUuY29tIiwidHJlYXN1cnltYW5hZ2VtZW50Lm1haG9wYWNiYW5rLmNvbSIsInRyZWFzdXJ5bWFuYWdlbWVudC5wZWdhc3VzYmFua2RhbGxhcy5jb20iLCJ0cmVhc3VyeW1hbmFnZW1lbnQuc2ltbW9uc2JhbmsuY29tIiwidHJlYXN1cnltYW5hZ2VtZW50LnRvbXBraW5zYmFuay5jb20iLCJ0cmVhc3VyeW1hbmFnZW1lbnQudmlzdGJhbmsuY29tIiwidHJlYXN1cnlzdWl0ZS50d29yaXZlcnMuYmFuayIsInRyZWFzdXJ5LmNpdGl6ZW5zbGMuY29tIiwidHJlYXN1cnkub3hmb3JkYmFuay5iYW5rIiwidHJlYXN1cnkucmVnZW50LmJhbmsiLCJ0dHMuaWZiYmFuay5jb20iLCJ2YXVsdHByby5wb3J0cmFpdGJhbmsuY29tIl19LCJncG8iOiI0NDciLCJvcmdhbml6YXRpb24iOiJNaXRlayBTeXN0ZW1zIEluYy4iLCJ2ZXJzaW9uIjoiMS4wIn19';
            case Environment.Local:
                return 'eyJzaWduYXR1cmUiOiJRd0x2QzdlNEZ1eHp0UVg5YkwrM0dZR21KK1pQQmY4YUJsM3E4amtLSitlUzNBd3ZYeFhkaE9jcE9FLzY1Q1o5MXJuUlcrNmFZNWtYRkFHNFVzOWVOSlpUYVlrQnZzaXZLZjF5U1E3T0ZFUFdsMEdoNU44NW9uV2JVMlErYkJKaFY2bXNlb0dKM3dtT01Xc1hCNjlBb240RTE3TGRHdzNJMmJiWUgxZkxNOE9sVjhtc3U3WHlnUnhkcVBrMDROZlp0cFBGR0h2bEVWbjF5TEZzRVZVUmZCaXhZM1BTK0g5YzU3SnAxVWFHWDJFdU05S3g0c05rbkc5SkVRZEF3cGVnSmtKODV2QTdua1prUDNDbFdxaTlZSW5Md2pmQ0VVSHFtWnl3L0gxbmFTeUxKUURoNHdGbkJmbFZieGpXblFPV1c5djBJREQva29aVFVIR1V0di9TOUE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI2LTA3LTMxIiwiZmVhdHVyZXMiOlsiZGVwb3NpdCIsImVtIl0sImFwcGxpY2F0aW9uSWRlbnRpZmllciI6eyJ3ZWIiOlsiMTAuMC4xLjE1IiwiMTkyLjE2OC4xLjE1Il19LCJncG8iOiI0NDciLCJvcmdhbml6YXRpb24iOiJNaXRlayBTeXN0ZW1zIEluYy4iLCJ2ZXJzaW9uIjoiMS4wIn19';
            default:
                throw new Error('Could not retrieve RDC SDK token.');
        }
    }

    public get version() {
        return this.injectedMetadata.version;
    }

    private get baseUrl() {
        const { protocol, hostname, port } = window.location;
        let url = `${protocol}//${hostname}`;

        if (port) {
            url += `:${port}`;
        }

        return url;
    }

    private get generateSsoCallbackUrl() {
        let { host } = window.location;
        if (this.app === AppType.Pwa) {
            host = `${host}/pwa`;
        }
        // TODO: remove bff condition once all apps are migrated to bff authentication
        if (this.apiRoot.startsWith('/bff')) {
            return `${window.location.protocol}//${host}/${this.institutionId}/login/bff`;
        }
        return `${window.location.protocol}//${host}/${this.institutionId}/login/sso`;
    }

    private get injectedConfig() {
        return (window as unknown as InjectedWindow).config ?? {};
    }

    private get injectedMetadata() {
        return (window as unknown as InjectedWindow).treasury ?? {};
    }
}

const buildType = BuildTypeToken.getValue() || BuildType.Unknown;
const windowService = new WindowService();

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const channelApiRoot = new ConfigurationService(
    AppType.Channel,
    buildType,
    windowService,
    new SessionStorageService(windowService)
).apiRoot;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const boApiRoot = new ConfigurationService(
    AppType.BackOffice,
    buildType,
    windowService,
    new SessionStorageService(windowService)
).apiRoot;
